import { useCallback, useState, ReactElement } from 'react';
import { Button, Card } from '@mui/material';
import { DEFAULT_EC2_ROUTES } from '@localstack/constants';
import { useAwsEffect, useAwsGetter, useRoutes } from '@localstack/services';
import { ButtonWithDialog, Dropdown, PageTitle, VPCsTable } from '@localstack/ui';

import { NavTabs } from './components/NavTabs';
import { EC2Props } from './types';

export const VPCs = ({ Layout, clientOverrides, routes = DEFAULT_EC2_ROUTES }: EC2Props): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { goto } = useRoutes();
  const { data: vpcs, isLoading, mutate } = useAwsGetter('EC2', 'describeVpcs', [], { clientOverrides });
  const { deleteVpc } = useAwsEffect('EC2', ['deleteVpc'], {
    revalidate: ['describeVpcs'],
    clientOverrides,
  });

  const vpcList = vpcs?.Vpcs ?? [];

  const handleDeleteSelected = useCallback(async () => {
    await Promise.all(selectedIds.map((VpcId) => deleteVpc({ VpcId })));
  }, [selectedIds]);

  return (
    <Layout
      documentTitle="EC2: VPCs"
      title={<PageTitle title="VPCs" onMutate={mutate} />}
      tabs={<NavTabs routes={routes} />}
      actions={
        <>
          <Button variant="outlined" onClick={() => goto(routes.RESOURCES_EC2_VPC_CREATE)}>
            Create VPC
          </Button>
          <Dropdown label="Actions">
            <ButtonWithDialog
              componentType="MenuItem"
              disabled={selectedIds.length === 0 || isLoading}
              dialogTitle={`Delete ${selectedIds.length} VPC(s)?`}
              onClick={handleDeleteSelected}
              dialogContent="Selected VPC(s) will be permanently deleted"
            >
              Remove Selected
            </ButtonWithDialog>
          </Dropdown>
        </>
      }
    >
      <Card>
        <VPCsTable
          vpcs={vpcList}
          loading={isLoading}
          onSelect={setSelectedIds}
          onViewVpc={({ VpcId }) => VpcId && goto(routes.RESOURCES_EC2_VPC, { vpcId: VpcId })}
        />
      </Card>
    </Layout>
  );
};
